import React, { useState } from 'react'
import { Typography, Drawer, Image, Button, Avatar } from 'antd'
import { Research } from '../../services/models'
import dayjs from 'dayjs'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.bubble.css'
import 'dayjs/locale/ru'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { API_URL } from '../../services/apiService'
import ResearchFileListComponent from '../ResearchFileListComponent/ResearchFileListComponent'
import { ZoomOutOutlined, ZoomInOutlined } from '@ant-design/icons'

dayjs.extend(localizedFormat)

// dayjs(undefined) подставляет текущую дату, поэтому отсутствие updatedAt
// обязательно обрабатывается явно — иначе вышел бы ложный «обновлено: сегодня».
const isEdited = (research: Research) => !!research.updatedAt && dayjs(research.updatedAt).isAfter(dayjs(research.createdAt))

// Разговорный формат «6 октября 2026» — тот же, что в карточке листинга,
// чтобы один и тот же факт читался одинаково в обоих местах.
const formatDate = (value: string) => dayjs(value).locale('ru').format('D MMMM YYYY')

const META_FONT_SIZE = 13
const TITLE_FONT_SIZE = 22
// Строки ~70 символов — предел, за которым текст читается тяжело. Без этого
// ограничения body растягивается на всю ширину окна и строки идут по 150+.
const BODY_MAX_WIDTH = 760

// Разметка приходит из редактора и в сидах, поэтому отступы задаются здесь
// явно: ReactQuill больше не оборачивает контент в .ql-editor со своими
// padding и line-height.
const bodyBlockStyle: React.CSSProperties = {
  maxWidth: BODY_MAX_WIDTH,
  lineHeight: 1.65,
  fontSize: 15,
  color: 'rgba(0, 0, 0, 0.88)'
}
type Props = {
    research: Research;
    // В админке заголовок уже рисует AdminPageTitle, поэтому там его
    // нужно отключить, иначе он продублируется.
    showTitle?: boolean;
}

const ResearchComponent: React.FC<Props> = ({ research, showTitle = true }) => {
  const { title, body, files, user, images, imageDescription } = research
  const [open, setOpen] = useState(false)

  const showDrawer = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  // Метаданные одной строкой, как в карточке листинга: подписи «Дата:» и
  // «Автор:» не несут смысла и лишь занимают место.
  const renderMeta = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 8 }}>
      <Avatar
        size={40}
        style={{ flex: '0 0 auto' }}
        src={`${API_URL}/avatar/${user.id}`}
        onClick={showDrawer}
      />
      <div style={{ fontSize: META_FONT_SIZE, color: 'rgba(0, 0, 0, 0.45)' }}>
        <span onClick={showDrawer} style={{ cursor: 'pointer' }}>
          {user.firstName} {user.lastName}
        </span>
        <span> · {formatDate(research.createdAt)}</span>
        {isEdited(research) && <span> · обновлено {formatDate(research.updatedAt)}</span>}
      </div>
    </div>
  )

  return (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
                {showTitle && (
                    <Typography.Title level={2} style={{ margin: 0, fontSize: TITLE_FONT_SIZE, lineHeight: 1.35 }}>
                        {title}
                    </Typography.Title>
                )}
                {renderMeta()}
                {!!files.length && (
                    <div style={{ marginTop: 12 }}>
                        <ResearchFileListComponent files={files}/>
                    </div>
                )}
              <ReactQuill
                value={body}
                readOnly={true}
                theme="bubble"
              />
            </div>

            {/* Галерея резервировала 250px даже без картинок — у части
                исследований их нет, и справа оставался пустой блок. */}
            {!!images.length && (
                <div style={{ width: '250px', minWidth: '250px' }}>
                    <Image.PreviewGroup
                        preview={{
                          countRender: (current, total) => {
                            const image = images[current - 1]
                            return (
                                <div style={{
                                  textAlign: 'center',
                                  backgroundColor: 'rgba(0,0,0,0.2)',
                                  borderRadius: '6px',
                                  padding: '10px'
                                }}>
                                    <Typography.Text
                                        style={{ color: 'white' }}>{current} / {total} {image && imageDescription[image.id] ? ` - ${imageDescription[image.id]}` : ''}</Typography.Text>
                                </div>
                            )
                          },
                          toolbarRender: (on, info) => {
                            return (
                                <div style={{
                                  textAlign: 'center',
                                  backgroundColor: 'rgba(0,0,0,0.2)',
                                  borderRadius: '6px'
                                }}>
                                    <Button type="text" onClick={info.actions.onZoomOut}
                                            icon={<ZoomOutOutlined style={{ color: 'white' }}/>}/>
                                    <Button type="text" onClick={info.actions.onZoomIn}
                                            icon={<ZoomInOutlined style={{ color: 'white' }}/>}/>
                                </div>
                            )
                          }
                        }}>
                        {images.map((image, i) => (
                            <div key={`image-${i}`} style={{
                              textAlign: 'left',
                              padding: '10px',
                              backgroundColor: 'white',
                              marginBottom: '10px'
                            }}>
                                <Image style={{ width: '100%' }} src={`${API_URL}/${image.path}`}/>
                                <Typography.Text>{imageDescription[image.id]}</Typography.Text>
                            </div>
                        ))}
                    </Image.PreviewGroup>
                </div>
            )}

            <Drawer title="Автор" placement="right" onClose={onClose} open={open}>
                <Typography.Title level={4} style={{ margin: 0 }}>{user.firstName} {user.lastName}</Typography.Title>
                <Typography.Title level={5} style={{ margin: 0 }}>{user.job}</Typography.Title>
                <div style={{ padding: '10px' }}>
                    <Image width={100} src={`${API_URL}/avatar/${user.id}`}/>
                </div>
                <Typography.Paragraph>{user.biography}</Typography.Paragraph>
                {user.contactEmail && (
                    <div style={{ marginTop: 8 }}>
                        <Typography.Text strong>email: </Typography.Text>
                        <Typography.Text copyable>{user.contactEmail}</Typography.Text>
                    </div>
                )}
            </Drawer>
        </div>
  )
}

export default ResearchComponent
